import { sessionExpiredService } from '../services/sessionExpiredService';
import { globalLoadingService } from '../services/globalLoading';
import { AuthenticationError } from './authErrors';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const AUTH_LOGIN_PATHS = [
  '/api/auth/login',
  '/api/practitioner-auth/login',
  '/api/employee-auth/login',
];
const AUTH_REFRESH_PATHS = [
  '/api/auth/refresh-token',
  '/api/practitioner-auth/refresh-token',
  '/api/employee-auth/refresh-token',
];
const PUBLIC_AUTH_PATHS = [
  ...AUTH_LOGIN_PATHS,
  ...AUTH_REFRESH_PATHS,
  '/api/auth/forgot-password',
  '/api/practitioner-auth/forgot-password',
  '/api/client-auth/forgot-password',
  '/api/auth/reset-password',
  '/api/practitioner-auth/reset-password',
  '/api/client-auth/reset-password',
];

let refreshPromise: Promise<string | null> | null = null;

const getApiUrl = (endpoint: string): string => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}${endpoint}`;
  }
  return endpoint;
};

const hasAuthPath = (url: string, paths: string[]): boolean => paths.some((path) => url.includes(path));

const getRefreshEndpoint = (): string => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.userType === 'PRACTITIONER') {
        return getApiUrl('/api/practitioner-auth/refresh-token');
      }
      if (payload.userType === 'EMPLOYEE') {
        return getApiUrl('/api/employee-auth/refresh-token');
      }
    }
  } catch {
    // Ignore malformed tokens and fall back to the admin endpoint.
  }

  return getApiUrl('/api/auth/refresh-token');
};

const normalizeHeaders = (headers?: HeadersInit): Headers => {
  if (!headers) {
    return new Headers();
  }

  if (headers instanceof Headers) {
    return new Headers(headers);
  }

  if (Array.isArray(headers)) {
    return new Headers(headers);
  }

  return new Headers(Object.entries(headers));
};

const withAuthorizationHeader = (
  args: Parameters<typeof fetch>,
  token: string
): Parameters<typeof fetch> => {
  const [input, init] = args;

  if (input instanceof Request) {
    const headers = normalizeHeaders(init?.headers ?? input.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return [new Request(input, { ...init, headers })];
  }

  const headers = normalizeHeaders(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return [input, { ...init, headers }];
};

const silentRefresh = async (originalFetch: typeof fetch): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await originalFetch(getRefreshEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store',
      },
      body: JSON.stringify({ refreshToken }),
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.success || !data?.token) {
      return null;
    }

    localStorage.setItem('token', data.token);
    if (data.refreshToken) {
      localStorage.setItem('refresh_token', data.refreshToken);
    }

    return data.token;
  } catch {
    return null;
  }
};

const refreshAccessToken = async (originalFetch: typeof fetch): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = silentRefresh(originalFetch).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

/**
 * Handle authentication errors (401) by showing session expired modal.
 * Permission errors (403) are returned to callers — they must not force logout.
 */
const handleAuthError = (message: string = 'Votre session a expiré. Veuillez vous reconnecter.') => {
  sessionExpiredService.showSessionExpired(message);
};

/**
 * Intercept fetch calls to handle 401 authentication errors globally.
 * 403 responses are authorization failures and are left to the calling code.
 */
const hasSkipGlobalLoadingHeader = (headers: HeadersInit | undefined): boolean => {
  if (!headers) return false;
  if (headers instanceof Headers) {
    return headers.get('x-skip-global-loading') === 'true';
  }
  if (Array.isArray(headers)) {
    return headers.some(([key, value]) => key.toLowerCase() === 'x-skip-global-loading' && value === 'true');
  }
  return Object.entries(headers).some(
    ([key, value]) => key.toLowerCase() === 'x-skip-global-loading' && value === 'true'
  );
};

export const setupFetchInterceptor = () => {
  // Store the original fetch function
  const originalFetch = window.fetch;
  
  // Override the global fetch function
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const url = typeof args[0] === 'string'
      ? args[0]
      : args[0] instanceof Request
        ? args[0].url
        : '';
    const isApiCall = url.includes('/api/');
    const method = typeof args[1]?.method === 'string' ? args[1]!.method!.toUpperCase() : 'GET';
    const skipGlobalLoading = hasSkipGlobalLoadingHeader(args[1]?.headers);
    const shouldBlock = isApiCall && method !== 'GET' && !skipGlobalLoading;

    if (shouldBlock) {
      globalLoadingService.start();
    }

    try {
      const response = await originalFetch(...args);

      const isPublicAuthRequest = hasAuthPath(url, PUBLIC_AUTH_PATHS);

      // Intercept protected API auth failures, but leave public auth flows to their pages.
      if (isApiCall && !isPublicAuthRequest) {
        if (response.status === 401) {
          const refreshedToken = await refreshAccessToken(originalFetch);
          if (refreshedToken) {
            const retried = await originalFetch(...withAuthorizationHeader(args, refreshedToken));
            if (retried.status !== 401) {
              return retried;
            }
          }

          // Prefer a user-facing message; API bodies often say "Invalid or expired token".
          if (!sessionExpiredService.getIsShowing()) {
            handleAuthError('Votre session a expiré. Veuillez vous reconnecter.');
          }
          // Stop callers from treating expiry as a generic load/action failure.
          throw new AuthenticationError();
        }
      }

      return response;
    } finally {
      if (shouldBlock) {
        globalLoadingService.stop();
      }
    }
  };
};

